import type {
  ConsoleConversationListQuery,
  ConsoleConversationSummary,
  ConversationId,
} from '@plato/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { ActionError, useAction } from '../../components/action.tsx';
import { DataTable } from '../../components/data-table.tsx';
import { formatCount, formatDateTime } from '../../components/format.ts';
import { useConsole } from '../../data/console-context.tsx';

type Filter = Omit<ConsoleConversationListQuery, 'limit'>;

export function conversationHref(conversation: ConversationId, user: string): string {
  return `#/conversations/${conversation}?user=${encodeURIComponent(user)}`;
}

export function ConversationList() {
  const { data, tenant } = useConsole();
  const [filter, setFilter] = useState<Filter>({});
  const [rows, setRows] = useState<readonly ConsoleConversationSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loadMore = useAction();

  useEffect(() => {
    let current = true;
    setRows(null);
    setError(null);
    data.listConversations(tenant.id, filter).then(
      (page) => {
        if (!current) return;
        setRows(page.data);
        setTotal(page.total);
        setCursor(page.next_cursor);
      },
      (cause: unknown) =>
        current && setError(cause instanceof Error ? cause.message : 'Could not load them.'),
    );
    return () => {
      current = false;
    };
  }, [data, tenant, filter]);

  const showMore = () =>
    void loadMore.run(async () => {
      if (cursor === null) return;
      const page = await data.listConversations(tenant.id, { ...filter, cursor });
      setRows((previous) => [...(previous ?? []), ...page.data]);
      setCursor(page.next_cursor);
    });

  return (
    <>
      <Filters onApply={setFilter} />
      {error !== null ? (
        <p role="alert">{error}</p>
      ) : rows === null ? (
        <p role="status">Loading…</p>
      ) : (
        <>
          <p className="muted">
            {formatCount(total)} {total === 1 ? 'conversation' : 'conversations'}
          </p>
          <DataTable
            caption="Conversations, newest activity first"
            columns={[
              { header: 'Last activity', cell: (row) => formatDateTime(row.updated_at) },
              {
                header: 'Conversation',
                cell: (row) => (
                  <>
                    <a href={conversationHref(row.conversation_id, row.user_id)}>
                      {row.title ?? 'Untitled'}
                    </a>
                    {row.purpose === 'scheduled' && <span className="chip">Scheduled</span>}
                  </>
                ),
              },
              { header: 'User', cell: (row) => <code>{row.user_subject}</code> },
              { header: 'Client', cell: (row) => row.organization ?? '—' },
              { header: 'Replies', cell: (row) => formatCount(row.runs) },
              { header: 'Tool calls', cell: (row) => formatCount(row.tool_calls) },
              { header: 'Rating', cell: ratingCell },
              { header: 'Errors', cell: errorCell },
            ]}
            rows={rows.map((row) => ({ ...row, id: row.conversation_id }))}
          />
          {rows.length === 0 && <p className="muted">No conversations match.</p>}
          {cursor !== null && (
            <button type="button" onClick={showMore} disabled={loadMore.pending}>
              {loadMore.pending ? 'Loading…' : 'Show more'}
            </button>
          )}
          <ActionError error={loadMore.error} />
        </>
      )}
    </>
  );
}

function ratingCell(row: ConsoleConversationSummary) {
  if (row.thumbs_up + row.thumbs_down === 0) return <span className="muted">—</span>;
  return (
    <span className="row">
      {row.thumbs_up > 0 && <span className="chip chip-success">{row.thumbs_up} up</span>}
      {row.thumbs_down > 0 && <span className="chip chip-danger">{row.thumbs_down} down</span>}
    </span>
  );
}

function errorCell(row: ConsoleConversationSummary) {
  const failures = row.failed_runs + row.failed_tool_calls;
  if (failures === 0) return <span className="muted">None</span>;
  return <span className="chip chip-danger">{failures} failed</span>;
}

function Filters({ onApply }: { onApply: (filter: Filter) => void }) {
  const apply = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => {
      const value = String(form.get(name) ?? '').trim();
      return value === '' ? undefined : value;
    };
    const rating = text('rating') as Filter['rating'];
    const errors = text('errors');
    const organization = text('organization');
    const from = text('from');
    const to = text('to');
    onApply({
      ...(rating !== undefined ? { rating } : {}),
      ...(errors !== undefined ? { has_error: errors === 'with' ? 'true' : 'false' } : {}),
      ...(organization !== undefined ? { organization } : {}),
      ...(from !== undefined ? { from } : {}),
      ...(to !== undefined ? { to } : {}),
    });
  };
  return (
    <form className="filters" onSubmit={apply} aria-label="Filter conversations">
      <label>
        <span className="label">Rating</span>
        <select name="rating" defaultValue="">
          <option value="">Any</option>
          <option value="down">Rated down</option>
          <option value="up">Rated up</option>
          <option value="none">Not rated</option>
        </select>
      </label>
      <label>
        <span className="label">Errors</span>
        <select name="errors" defaultValue="">
          <option value="">Any</option>
          <option value="with">With errors</option>
          <option value="without">Without errors</option>
        </select>
      </label>
      <label>
        <span className="label">Client</span>
        <input name="organization" placeholder="Organization id" />
      </label>
      <label>
        <span className="label">From</span>
        <input name="from" type="date" />
      </label>
      <label>
        <span className="label">Before</span>
        <input name="to" type="date" />
      </label>
      <button type="submit">Apply</button>
    </form>
  );
}
